import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export interface DropdownItem {
  id: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Tooltip, e.g. why the item is disabled. */
  title?: string;
  tone?: 'default' | 'danger';
  /** Renders a separator above this item. */
  separatorBefore?: boolean;
}

interface TriggerProps {
  ref: React.RefObject<HTMLButtonElement>;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
  id: string;
  onClick: () => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLButtonElement>) => void;
}

interface DropdownMenuProps {
  items: readonly DropdownItem[];
  renderTrigger: (props: TriggerProps) => ReactNode;
  align?: 'start' | 'end';
  label?: string;
  header?: ReactNode;
  minWidth?: number;
}

const GAP = 6;

/**
 * Accessible menu button (WAI-ARIA "menu button" pattern).
 * Rendered in a portal with fixed positioning so it is never clipped by
 * scrollable table containers. Supports Arrow keys, Home/End, Escape, Tab and
 * type-ahead.
 */
export function DropdownMenu({ items, renderTrigger, align = 'end', label, header, minWidth = 208 }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState<{ top: number; left: number; placement: 'bottom' | 'top' } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  const triggerId = useId();

  const enabledIndexes = items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const openMenu = (focus: 'first' | 'last' = 'first') => {
    setOpen(true);
    setActiveIndex(focus === 'first' ? (enabledIndexes[0] ?? -1) : (enabledIndexes[enabledIndexes.length - 1] ?? -1));
  };

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu.offsetHeight;
    const menuWidth = menu.offsetWidth;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement = spaceBelow < menuHeight + GAP + 8 && rect.top > menuHeight + GAP ? 'top' : 'bottom';
    const top = placement === 'bottom' ? rect.bottom + GAP : rect.top - menuHeight - GAP;
    let left = align === 'end' ? rect.right - menuWidth : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    setPosition({ top, left, placement });
  }, [align]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close(false);
    };
    const onScrollOrResize = () => updatePosition();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, close, updatePosition]);

  useEffect(() => {
    if (open && activeIndex >= 0) itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex, position]);

  const move = (delta: 1 | -1) => {
    if (enabledIndexes.length === 0) return;
    const current = enabledIndexes.indexOf(activeIndex);
    const next = current === -1 ? 0 : (current + delta + enabledIndexes.length) % enabledIndexes.length;
    setActiveIndex(enabledIndexes[next]!);
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(enabledIndexes[0] ?? -1);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(enabledIndexes[enabledIndexes.length - 1] ?? -1);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close();
        break;
      case 'Tab':
        close(false);
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const char = e.key.toLowerCase();
          const start = enabledIndexes.indexOf(activeIndex);
          const ordered = [...enabledIndexes.slice(start + 1), ...enabledIndexes.slice(0, start + 1)];
          const match = ordered.find((i) => items[i]!.label.toLowerCase().startsWith(char));
          if (match !== undefined) setActiveIndex(match);
        }
    }
  };

  const select = (item: DropdownItem) => {
    if (item.disabled) return;
    close();
    item.onSelect();
  };

  const trigger = renderTrigger({
    ref: triggerRef,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    id: triggerId,
    onClick: () => (open ? close() : openMenu('first')),
    onKeyDown: (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        openMenu('first');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        openMenu('last');
      }
    },
  });

  return (
    <>
      {trigger}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            aria-labelledby={label ? undefined : triggerId}
            onKeyDown={onMenuKeyDown}
            style={{
              position: 'fixed',
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              minWidth,
              visibility: position ? 'visible' : 'hidden',
            }}
            className="z-dropdown animate-scale-in rounded-lg border border-line bg-surface p-1 shadow-overlay"
          >
            {header && <div className="border-b border-line px-3 pb-2 pt-1.5">{header}</div>}
            {items.map((item, index) => (
              <Fragment key={item.id}>
                {item.separatorBefore && <div role="separator" className="my-1 h-px bg-line" />}
                <button
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  type="button"
                  role="menuitem"
                  tabIndex={index === activeIndex ? 0 : -1}
                  disabled={item.disabled}
                  aria-disabled={item.disabled || undefined}
                  title={item.title}
                  onClick={() => select(item)}
                  onMouseEnter={() => !item.disabled && setActiveIndex(index)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors focus:outline-none',
                    item.tone === 'danger' ? 'text-danger' : 'text-ink',
                    index === activeIndex && (item.tone === 'danger' ? 'bg-danger-soft' : 'bg-surface-sunken'),
                    item.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {item.icon && (
                    <span aria-hidden="true" className={cn('shrink-0', item.tone === 'danger' ? 'text-danger' : 'text-ink-muted')}>
                      {item.icon}
                    </span>
                  )}
                  <span className="truncate">{item.label}</span>
                </button>
              </Fragment>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
